/**
 * CORE-1730 / CORE-2058 — «гейт» плавающего меню сообщения на мобильном вебе.
 * Без React и RN: слушатели вешаются на любой EventTarget, поэтому поведение
 * проверяется в jest настоящей отправкой событий.
 *
 * Возврат QA 31.10: меню открывается долгим нажатием, и слой появляется под
 * ещё прижатым пальцем. По отпусканию браузер синтезирует совместимые
 * mousedown/mouseup/click уже по слою (подложке или, при карточке вверх, её
 * пункту) — и меню закрывалось или срабатывал пункт. Поэтому click по слою
 * проходит только после жеста, начатого на самом слое: `pointerdown`,
 * `touchstart`, `keydown` (не `mousedown` — его Chrome синтезирует после
 * касания по подложке, а pointerdown — нет).
 *
 * CORE-2058 — скринридеры активируют пункт без pointerdown:
 *  - «виртуальным» click (NVDA в режиме обзора, JAWS, Narrator, VoiceOver на
 *    Mac, `element.click()`): `detail === 0` и пустой `pointerType`;
 *    в Firefox — `mozInputSource === 0`;
 *  - парой mousedown + click без pointerdown (эмуляция мыши скринридером).
 * Такие активации проходят. Хвост открывающего нажатия от них отличается
 * тем, что идёт сразу за отпусканием пальца, начатого вне слоя: после такого
 * touchend/pointerup (касание, перо) окно `TOUCH_TAIL_MS` — в нём mousedown не
 * взводит слой и любой невзведённый click гасится. Сам хвостовой click
 * виртуальным не бывает (`detail` касания — 1), так что и без окна он гасится;
 * окно нужно для совместимого mousedown перед ним.
 * TalkBack и VoiceOver на iOS шлют pointerdown/touchstart — те взводят слой.
 */

/** События, которыми человек начинает свой жест уже на открытом слое. */
export const ARMING_EVENTS = ['pointerdown', 'touchstart', 'keydown'] as const;

/** Окно после отпускания пальца, в которое приходят совместимые mousedown/click. */
export const TOUCH_TAIL_MS = 1000;

/** Отпускание пальца или пера где угодно на странице. */
const TOUCH_END_EVENTS = ['touchend', 'touchcancel', 'pointerup', 'pointercancel'] as const;

/** Объектом, не `true`: EventTarget Node не снимает слушатель, поставленный булевым capture. */
const CAPTURE = { capture: true } as const;

type ClickLike = Event & { detail?: number; pointerType?: string; mozInputSource?: number };

/** Click, отправленный скринридером или кодом, а не указателем. */
export function isVirtualClick(e: ClickLike): boolean {
  if (e.mozInputSource === 0 && e.isTrusted) return true;
  return e.detail === 0 && !e.pointerType;
}

/**
 * Вешает гейт на `node` (слой меню) и слежение за отпусканием пальца на `doc`.
 * Возвращает снятие всех слушателей.
 */
export function installMenuGate(node: EventTarget, doc: EventTarget, now: () => number = Date.now): () => void {
  let armed = false;
  let tailUntil = 0;
  const inTail = () => now() < tailUntil;

  const arm = () => { armed = true; };
  const armByMouse = () => { if (!inTail()) armed = true; };
  const onTouchEnd = (e: Event) => {
    // Свой жест на слое уже взвёл его; мышиный pointerup хвоста не оставляет.
    if (armed) return;
    if (e.type.startsWith('pointer') && (e as ClickLike).pointerType === 'mouse') return;
    tailUntil = now() + TOUCH_TAIL_MS;
  };
  const swallow = (e: Event) => {
    if (armed) return;
    if (!inTail() && isVirtualClick(e as ClickLike)) return;
    tailUntil = 0;
    e.stopImmediatePropagation();
    e.preventDefault();
  };

  for (const type of ARMING_EVENTS) node.addEventListener(type, arm, CAPTURE);
  node.addEventListener('mousedown', armByMouse, CAPTURE);
  node.addEventListener('click', swallow, CAPTURE);
  for (const type of TOUCH_END_EVENTS) doc.addEventListener(type, onTouchEnd, CAPTURE);
  return () => {
    for (const type of ARMING_EVENTS) node.removeEventListener(type, arm, CAPTURE);
    node.removeEventListener('mousedown', armByMouse, CAPTURE);
    node.removeEventListener('click', swallow, CAPTURE);
    for (const type of TOUCH_END_EVENTS) doc.removeEventListener(type, onTouchEnd, CAPTURE);
  };
}
